<script lang="ts">
	import { tick } from 'svelte'
	import { trackElement } from '$lib/utils/domActions'
	import CounterBadge from '$lib/atoms/CounterBadge.svelte'

	interface Tab {
		label: string
		disabled?: boolean
		id?: string
		counter?: number // optional property, if given shows a small badge with the counter value
	}

	let {
		activeIndex = $bindable(),
		activeId = $bindable(),
		ariaLabel = 'Tab navigation',
		tabs,
		justify = 'center',
		onChange,
		...restProps
	}: {
		activeIndex?: number
		activeId?: string
		ariaLabel?: string
		tabs: Tab[]
		justify?: 'start' | 'center' | 'end'
		onChange?: (index: number) => void
	} = $props()

	let navEl: HTMLElement | undefined = $state()
	let buttonEls: (HTMLElement | undefined)[] = $state([])

	const firstEnabled = $derived(tabs.findIndex((t) => !t.disabled))
	const current = $derived.by(() => {
		if (activeIndex !== undefined) {
			return activeIndex
		}

		const activeIndexById = tabs.findIndex((tab) => tab.id === activeId)
		return activeIndexById === -1 ? firstEnabled : activeIndexById
	})

	/**
	 * sanctioned `$effect`-writes-state exception. seeds the bindable
	 * `activeIndex` / `activeId` when a consumer mounts without a valid
	 * selection (PopLoadedView passes `bind:activeIndex` starting undefined),
	 * or when the current selection points at a disabled or no-longer-existing
	 * tab (e.g. a caller re-derives `tabs` and drops the previously active one).
	 * a bindable prop that depends on `tabs` and must notify the parent via
	 * `onChange` cannot be seeded with `$derived` — do not "fix" this into a
	 * derived; the write-back to the bindings is the point.
	 */
	$effect(() => {
		const activeIndexTab = activeIndex !== undefined ? tabs[activeIndex] : undefined
		const activeIdTab = tabs.find((tab) => tab.id === activeId)

		if (
			(activeIndex === undefined || !activeIndexTab || activeIndexTab.disabled) &&
			(activeId === undefined || !activeIdTab || activeIdTab.disabled)
		) {
			const idx = firstEnabled
			if (idx >= 0) {
				activeIndex = idx
				activeId = tabs[idx]?.id
				onChange?.(idx)
			}
		}
	})

	const select = (index: number) => {
		if (tabs[index]?.disabled || current === index) return
		activeIndex = index
		activeId = tabs[index]?.id
		onChange?.(index)
	}

	const onKeydown = (event: KeyboardEvent) => {
		if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return
		event.preventDefault()

		let next = current
		if (event.key === 'Home') next = 0
		else if (event.key === 'End') next = tabs.length - 1
		else if (event.key === 'ArrowRight') next = (current + 1) % tabs.length
		else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length

		// skip disabled tabs
		const dir = event.key === 'ArrowLeft' ? -1 : 1
		let guard = 0
		while (guard < tabs.length && tabs[next]?.disabled) {
			next = (next + dir + tabs.length) % tabs.length
			guard++
		}
		if (guard >= tabs.length) return

		select(next)
		tick().then(() => buttonEls[next]?.focus())
	}
</script>

<nav
	class="tab-menu"
	bind:this={navEl}
	aria-label={ariaLabel}
	style:--tab-justify={justify}
	{...restProps}
>
	<ul role="tablist" onkeydown={onKeydown}>
		{#each tabs as tab, i (tab.id ?? i)}
			<li role="presentation">
				<button
					bind:this={buttonEls[i]}
					type="button"
					role="tab"
					id={tab.id}
					disabled={tab.disabled}
					aria-selected={current === i}
					tabindex={current === i ? 0 : -1}
					class:is-active={current === i}
					onclick={() => select(i)}
				>
					{tab.label}
					{#if tab.counter !== undefined && tab.counter !== null}
						<CounterBadge count={tab.counter} active={current === i} />
					{/if}
				</button>
			</li>
		{/each}
	</ul>
	<span
		class="indicator"
		aria-hidden="true"
		{@attach trackElement({
			target: () => buttonEls[current],
			container: () => navEl,
			vars: { x: '--tab-indicator-x', width: '--tab-indicator-width' }
		})}
	></span>
</nav>

<style lang="scss">
	@import '$styles/global';

	.tab-menu {
		--tab-accent: var(--menu-tab-accent, #32b24a);
		--tab-text-color: var(--menu-tab-text-color, black);
		--tab-text-muted: var(--menu-tab-muted, var(--color-gray-600));

		position: relative;
		border-bottom: 1px solid var(--menu-tab-border, var(--color-gray-100));

		& ul {
			display: flex;
			gap: 28px;
			list-style: none;
			margin: 0;
			padding: 0;
			justify-content: var(--tab-justify, start);
		}

		& ul > li > button {
			align-items: center;
			background: transparent;
			border: 0;
			cursor: pointer;
			display: inline-flex;
			column-gap: 6px;

			@include roboto-font(normal, 500, 1rem, var(--tab-text-color));

			transition: color 160ms ease;
			padding: 4px 4px 8px;
			border-radius: 4px;
		}

		& ul > li > button:not(:disabled):hover {
			background-color: var(--color-green-100);
		}

		& ul > li > button:disabled {
			color: var(--tab-text-muted);
			cursor: not-allowed;
		}

		& .indicator {
			position: absolute;
			left: 0;
			bottom: 0;
			height: 4px;
			width: var(--tab-indicator-width);
			background: var(--tab-accent);
			border-radius: 999px;
			transform: translateX(var(--tab-indicator-x));
			transition:
				transform 220ms ease,
				width 220ms ease;
		}
	}

	@media (width <= 640px) {
		.tab-menu ul {
			gap: 18px;
		}
	}
</style>
